/**
 * FeatureGrid — a responsive grid of feature cards (icon + title + body, with
 * an optional link). Composes Card + lucide icons (resolved by name) +
 * MarketingLink. Tokens + Tailwind only.
 */
import type { ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';
import { Card, CardTitle } from '../card';
import { getIconWithFallback } from '../../lib/icon-resolver';
import { MarketingLink } from '../marketing-link';
import { cn } from '../../utils';

export interface FeatureItem {
  readonly title: ReactNode;
  readonly body: ReactNode;
  /** lucide icon name (resolved via icon-resolver; falls back to HelpCircle). */
  readonly icon?: string;
  readonly href?: string;
  readonly index?: string;
}

export interface FeatureGridProps {
  readonly features: readonly FeatureItem[];
  readonly columns?: 2 | 3 | 4;
}

export function FeatureGrid({ features, columns = 3 }: FeatureGridProps) {
  const cols = { 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-2 lg:grid-cols-3', 4: 'sm:grid-cols-2 lg:grid-cols-4' }[columns];
  return (
    <div className={cn('grid grid-cols-1 gap-(--space-grid)', cols)}>
      {features.map((f, i) => {
        const Icon = f.icon ? getIconWithFallback(f.icon) : null;
        const body = (
          <Card key={i} className="h-full p-(--space-card) transition-colors hover:border-primary/40" interactive={!!f.href}>
            <div className="flex items-center gap-3.5">
              {Icon && (
                <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary-light text-primary">
                  <Icon className="size-5" aria-hidden="true" />
                </span>
              )}
              <div className="min-w-0 flex-1">
                {f.index && <span className="mb-1 block font-mono text-xs text-primary">{f.index}</span>}
                <CardTitle as="h3" className="text-base">{f.title}</CardTitle>
                {f.body && <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{f.body}</p>}
                {f.href && (
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary">
                    Learn more <ArrowRight className="size-4" aria-hidden="true" />
                  </span>
                )}
              </div>
            </div>
          </Card>
        );
        return f.href ? (
          <MarketingLink key={i} href={f.href} className="block">{body}</MarketingLink>
        ) : body;
      })}
    </div>
  );
}
